import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import CollectDataFilters from './components/collect-data-filters/CollectDataFilters';
import CollectDataTable from './components/collect-data-table/CollectDataTable';
import CommonButton from '../../../../components/common-button/CommonButton';
import {
  cancelProgress,
  collectData,
  getManagementDataset,
  getSonarProgress,
} from '../../../../redux/sonar-management/sonarManagementActions';
import { CRAWL_PROJECT_STATUS, SONAR_PROGRESS_TYPE } from '../../../../constants';
import CommonTooltip from '../../../../components/common-tooltip/CommonTooltip';
import { resetParams } from '../../../../redux/sonar-management/sonarManagementReducer';
import CommonIconButton from '../../../../components/common-icon-button/CommonIconButton';
import { ICONS } from '../../../../icons';
import CommonPage from '../../../../components/common-page/CommonPage';

import scss from './SonarCollectData.module.scss';

const SonarCollectData = () => {
  const { t } = useTranslation('projects');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const [selectedDataset, setSelectedDataset] = useState({});
  const crawlDataProgress = useSelector((state) => state.sonarManagement.sonarProgress[SONAR_PROGRESS_TYPE.CRAWL]);
  const collectDataProgress = useSelector(
    (state) => state.sonarManagement.sonarProgress[SONAR_PROGRESS_TYPE.COLLECT_SONAR],
  );

  const disableButtonStatuses = {
    [SONAR_PROGRESS_TYPE.CRAWL]: {
      [CRAWL_PROJECT_STATUS.EXECUTE]: t('thisProjectIsInCrawlQueueNow'),
      [CRAWL_PROJECT_STATUS.EXECUTING]: t('thisProjectIsCrawlingNow'),
    },
    [SONAR_PROGRESS_TYPE.COLLECT_SONAR]: {
      [CRAWL_PROJECT_STATUS.EXECUTE]: t('thisProjectIsInCollectDataQueueNow'),
      [CRAWL_PROJECT_STATUS.EXECUTING]: t('thisProjectIsCollectingDataNow'),
    },
  };

  const collectDataTypeLabel = {
    DAILY: t('daily'),
    MONTHLY: t('monthly'),
  };

  const disableActionButtons =
    crawlDataProgress.status === CRAWL_PROJECT_STATUS.EXECUTING ||
    crawlDataProgress.status === CRAWL_PROJECT_STATUS.EXECUTE ||
    collectDataProgress.status === CRAWL_PROJECT_STATUS.EXECUTING ||
    collectDataProgress.status === CRAWL_PROJECT_STATUS.EXECUTE;

  const disableTooltip =
    disableButtonStatuses[SONAR_PROGRESS_TYPE.CRAWL][crawlDataProgress.status] ||
    disableButtonStatuses[SONAR_PROGRESS_TYPE.COLLECT_SONAR][collectDataProgress.status] ||
    null;

  const goProjects = () => {
    navigate('/projects');
  };

  const handleCollectData = () => {
    dispatch(collectData(id));
  };

  const handleCancelCollectingData = () => {
    dispatch(cancelProgress({ progressId: collectDataProgress.id, jobEntityId: id }));
  };

  const handleEditDatasetStart = (dataset) => {
    setSelectedDataset(dataset);
  };

  const handleEditDatasetEnd = () => {
    setSelectedDataset({});
  };

  useEffect(() => {
    if (id) {
      dispatch(getManagementDataset(id));
      dispatch(getSonarProgress(id));
    }

    return () => {
      dispatch(resetParams());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getCrawlProgressInterval = setInterval(() => {
      dispatch(getSonarProgress(id));
    }, 10000);

    return () => {
      clearInterval(getCrawlProgressInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CommonPage>
      <h3 className={scss.pageTitle}>{t('dataVerification')}</h3>

      <CollectDataFilters
        disableTooltip={disableTooltip}
        disableActionButtons={disableActionButtons}
        onEditDatasetEnd={handleEditDatasetEnd}
        selectedDataset={selectedDataset}
      />

      <CollectDataTable disableActionButtons={disableActionButtons} onEditStart={handleEditDatasetStart} />

      <div className={scss.actions}>
        <div className={scss.actionsLeftBlock}>
          <CommonTooltip title={disableTooltip}>
            <span style={{ width: '160px' }}>
              <CommonButton
                type='button'
                styleColor='primary'
                maxWidth={160}
                onClick={handleCollectData}
                isLoading={
                  collectDataProgress.status === CRAWL_PROJECT_STATUS.EXECUTING ||
                  collectDataProgress.status === CRAWL_PROJECT_STATUS.EXECUTE
                }
                disabled={
                  crawlDataProgress.status &&
                  (crawlDataProgress.status === CRAWL_PROJECT_STATUS.EXECUTE ||
                    crawlDataProgress.status === CRAWL_PROJECT_STATUS.EXECUTING)
                }>
                {t('collectData')}
              </CommonButton>
            </span>
          </CommonTooltip>

          {collectDataProgress.status === CRAWL_PROJECT_STATUS.EXECUTING ? (
            <span className={scss.progress}>
              {collectDataTypeLabel[collectDataProgress.currentProgress.type]}{' '}
              {t('progressOf', { from: collectDataProgress.currentProgress.count, to: collectDataProgress.total })}
            </span>
          ) : null}

          {collectDataProgress.status === CRAWL_PROJECT_STATUS.EXECUTING ||
          collectDataProgress.status === CRAWL_PROJECT_STATUS.EXECUTE ? (
            <CommonIconButton
              icon={ICONS.Cross}
              onClick={handleCancelCollectingData}
              isStrokeIcon
              size='large'
              iconClassName={scss.cancelIcon}
              tooltip={t('cancelCollectingData')}
            />
          ) : null}
        </div>

        <CommonButton type='button' styleColor='secondary' maxWidth={160} onClick={goProjects}>
          {t('cancel')}
        </CommonButton>
      </div>
    </CommonPage>
  );
};

export default SonarCollectData;
